// src/pages/HraAnalyticsPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import BarChartComponent from "../../components/results/barChartComponent";
import analyticsApi from "../../api/analyticsApi";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";

import ageRanges from "../../data/ageRanges";
import mexicoStates from "../../data/mexicoStates";
import workScheme from "../../data/workScheme";
import biologicalSexList from "../../data/biologicalSex";
import jobArea from "../../data/jobArea";

import styles from "./HraAnalyticsPage.module.css";

const HraAnalyticsPage = () => {
  // companyId desde URL o state
  const { companyId: paramCompanyId, branchId } = useParams();
  const { state } = useLocation();
  const companyId = paramCompanyId || state?.companyId;
  const analyticsScopeId = branchId || companyId;
  const areaScopeId = analyticsScopeId;

  // Datos de analítica
  const [analyticsData, setAnalyticsData] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtros sincronizados con query string
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState({
    ageRange: searchParams.get("ageRange") || "",
    residenceState: searchParams.get("residenceState") || "",
    workScheme: searchParams.get("workScheme") || "",
    biologicalSex: searchParams.get("biologicalSex") || "",
    jobArea: searchParams.get("jobArea") || ""
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...selectedFilters, [name]: value };
    setSelectedFilters(newFilters);

    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(name, value);
    else newParams.delete(name);
    setSearchParams(newParams);
  };

  // Datos de la empresa
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [companyName, setCompanyName] = useState({});

  // Listado de respondants HRA
  const [respondents, setRespondents] = useState([]);
  const [empSortBy, setEmpSortBy] = useState("email");
  const [empSortOrder, setEmpSortOrder] = useState("asc");

  // Estados para paginación local
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Descarga Excel
  const [downloading, setDownloading] = useState(false);

  // Opciones para área de trabajo
  const jobAreaOptions = companyJobAreas.length ? companyJobAreas : jobArea;

  // 1) Cargar datos de la empresa
  useEffect(() => {
    if (!areaScopeId) return;
    (async () => {
      try {
        const resp = await questionnaireScopeApi.getById(areaScopeId);
        setCompanyName(resp);
        setCompanyJobAreas(resp.jobAreas || []);
      } catch (err) {
        console.error("Error cargando empresa/sucursal:", err);
      }
    })();
  }, [areaScopeId]);

  // 2) Cargar analítica cuando cambian filtros o companyId
  useEffect(() => {
    if (!analyticsScopeId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await analyticsApi.getHraAnalyticsByCompany(
          analyticsScopeId,
          selectedFilters
        );
        if (initialTotal === null && data?.totalRespondents != null) {
          setInitialTotal(data.totalRespondents);
        }
        setAnalyticsData(data);
      } catch (err) {
        console.error("Error cargando datos analíticos HRA:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [analyticsScopeId, selectedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3) Cargar lista de respondants HRA
  useEffect(() => {
    if (!analyticsScopeId) return;
    (async () => {
      try {
        const list = await analyticsApi.getHraRespondantsByCompanyId(analyticsScopeId);
        setRespondents(list);
      } catch (err) {
        console.error("Error obteniendo lista de empleados HRA:", err);
      }
    })();
  }, [analyticsScopeId]);

  // Ordenar todos los respondientes
  const sortedRespondents = useMemo(() => {
    return [...respondents].sort((a, b) => {
      const field = empSortBy;
      if (a[field] < b[field]) return empSortOrder === "asc" ? -1 : 1;
      if (a[field] > b[field]) return empSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [respondents, empSortBy, empSortOrder]);

  // Paginación: calcular total de páginas y rebanar
  const totalPages = Math.ceil(sortedRespondents.length / pageSize) || 1;
  const displayedRespondents = sortedRespondents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onSort = (column) => {
    if (empSortBy === column) {
      setEmpSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setEmpSortBy(column);
      setEmpSortOrder("asc");
    }
  };

  const SortArrow = ({ column }) => {
    const active = empSortBy === column;
    if (!active) {
      return (
        <span className={styles.sortArrow}>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        </span>
      );
    }
    return (
      <span className={styles.sortArrow}>
        {empSortOrder === "asc" ? (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        )}
      </span>
    );
  };

  const renderFilter = (label, name, options) => (
    <div className={styles.filterItem} key={name}>
      <label htmlFor={name} className={styles.filterLabel}>
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={selectedFilters[name]}
        onChange={handleFilterChange}
        className={styles.select}
      >
        <option value="">Todos</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );

  const handleDownloadExcel = async () => {
    if (!analyticsScopeId) return;
    try {
      setDownloading(true);
      await analyticsApi.downloadHraRespondantsByCompanyIdExcel(analyticsScopeId);
    } catch (err) {
      console.error("Error descargando Excel HRA:", err);
    } finally {
      setDownloading(false);
    }
  };

  const activeFilterCount = Object.values(selectedFilters).filter(Boolean).length;
  
    const clearFilters = () => {
      setSelectedFilters({
        jobArea: "",
        residenceState: "",
        ageRange: "",
        residenceState: "",
        biologicalSex: "",
      });
      setSearchParams(new URLSearchParams());
      setCurrentPage(1);
    };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>EVALUACION DE RIESGO EN SALUD</span>
          <h2>Health Risk Assessment</h2>
          <p className={styles.subheader}>
            Resultados de <span className={styles.companyId}>{companyName.name || "la empresa"}</span>
          </p>
        </div>
        <div className={styles.summaryCards}>
          {initialTotal !== null && (
            <div className={styles.summaryCard}>
              <span>Participantes iniciales</span>
              <strong>{initialTotal}</strong>
            </div>
          )}
          {!loading && analyticsData && (
            <div className={`${styles.summaryCard} ${styles.summaryCardAccent}`}>
              <span>Resultados visibles</span>
              <strong>{analyticsData.totalRespondents}</strong>
            </div>
          )}
        </div>
      </header>

      <section className={styles.filtersPanel}>
        <div className={styles.filtersHeader}>
          <div>
            <h3>Filtros generales</h3>
            <p>Refina todas las gráficas al mismo tiempo.</p>
          </div>
          <button type="button" className={styles.clearFilters} onClick={clearFilters} disabled={activeFilterCount === 0}>
            Limpiar filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
        <div className={styles.filters}>
        {renderFilter("Rango de Edad", "ageRange", ageRanges)}
        {renderFilter("Estado de Residencia", "residenceState", mexicoStates)}
        {renderFilter("Esquema Laboral", "workScheme", workScheme)}
        {renderFilter("Sexo Biológico", "biologicalSex", biologicalSexList)}
        {renderFilter("Área de Trabajo", "jobArea", jobAreaOptions)}
        </div>
      </section>

      {!loading && analyticsData && (
        <div className={styles.totalCapsule}>
          Total encuestados: <span>{analyticsData.totalRespondents}</span>
        </div>
      )}

      <section className={styles.content}>
        <div className={styles.sectionHeading}>
          <div>
            <span className={styles.sectionEyebrow}>RESULTADOS</span>
            <h3>Resultados Encuesta HRA</h3>
          </div>
          {!loading && analyticsData && <span className={styles.resultCount}>{analyticsData.totalRespondents} respuestas</span>}
        </div>
        {loading ? (
          <div className={styles.loadingCard}>Cargando gráficas...</div>
        ) : analyticsData ? (
          <div className={styles.charts}>
            <BarChartComponent
              data={analyticsData.nutritionalState}
              title="Estado Nutricional"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.lifeStyle}
              title="Estilo de Vida"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.physicalActivityEvaluation}
              title="Actividad Física"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.workHappiness}
              title="Felicidad Laboral"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
            <BarChartComponent
              data={analyticsData.overallEvaluation}
              title="Evaluación General"
              variant="dashboard"
              twoLineLabels
              semanticColors
              downloadable
            />
          </div>
        ) : (
          <p className={styles.message}>No se pudieron cargar los datos.</p>
        )}
      </section>

      {/* Listado de Empleados (Respondants) */}
      <section className={styles.responsesSection}>
        <div className={styles.tableHeader}>
          <div>
            <span className={styles.sectionEyebrow}>DETALLE</span>
            <h3>Listado de empleados</h3>
            <p>{respondents.length} empleados registrados</p>
          </div>
          <button
            className={styles.exportButton}
            onClick={handleDownloadExcel}
            disabled={downloading || !analyticsScopeId}
            title="Exportar listado de empleados (HRA) a Excel"
          >
            {downloading ? "Descargando..." : "Exportar Excel"}
          </button>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th onClick={() => onSort("email")} className={styles.clickable}>
                  Correo <SortArrow column="email" />
                </th>
                <th onClick={() => onSort("status")} className={styles.clickable}>
                  Estado <SortArrow column="status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRespondents.length > 0 ? (
                displayedRespondents.map((r, i) => (
                  <tr key={r.email}>
                    <td>{(currentPage - 1) * pageSize + i + 1}</td>
                    <td>{r.email}</td>
                    <td>{r.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className={styles.message}>
                    No hay empleados para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de paginación + botón Excel */}
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          <span>
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
};

export default HraAnalyticsPage;
